/**
 * Brand List Component
 * Shows all brands and allows management
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

function BrandList() {
    const { getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadBrands();
    }, []);

    const loadBrands = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const response = await axios.get('/api/admin/brands', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBrands(response.data.brands || []);
            setError(null);
        } catch (err) {
            console.error('Error loading brands:', err);
            setError(err.response?.data?.message || 'Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleBrand = async (brandId, currentStatus) => {
        const action = currentStatus ? 'disable' : 'enable';
        const confirmed = window.confirm(
            `Are you sure you want to ${action} this brand?\n\n` +
            `${currentStatus ? '⚠️ Disabling will prevent all new messages and respond with "This chat is currently disabled."' : '✅ Enabling will allow customers to chat again.'}`
        );

        if (!confirmed) return;

        try {
            const token = await getAccessTokenSilently();
            await axios.patch(
                `/api/admin/brands/${brandId}/toggle`,
                { isActive: !currentStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local state
            setBrands(brands.map(brand =>
                brand._id === brandId
                    ? { ...brand, isActive: !currentStatus }
                    : brand
            ));
        } catch (err) {
            console.error('Error toggling brand:', err);
            alert('Failed to update brand status');
        }
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading brands...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-0">Brands</h2>
                    <p className="text-muted">Manage your brands and their active status</p>
                </div>
                <button className="btn btn-primary" onClick={loadBrands}>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                </button>
            </div>

            <div className="row g-4">
                {brands.map((brand) => (
                    <div key={brand._id} className="col-md-6 col-lg-4">
                        <div
                            className={`card border-0 shadow-sm h-100 ${!brand.isActive ? 'opacity-75' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/admin/brands/${brand._id}`)}
                        >
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <h5 className="card-title mb-0">
                                        <Link to={`/admin/brands/${brand._id}`} className="text-decoration-none text-dark">
                                            {brand.name}
                                        </Link>
                                    </h5>
                                    <span className={`badge ${brand.isActive ? 'bg-success' : 'bg-danger'}`}>
                                        {brand.isActive ? 'Active' : 'Disabled'}
                                    </span>
                                </div>

                                <p className="text-muted mb-2">
                                    <span className="badge bg-light text-dark">{brand.industry}</span>
                                </p>

                                <p className="mb-3">
                                    <small className="text-muted">📞 {brand.phone}</small>
                                </p>

                                {!brand.isActive && (
                                    <div className="alert alert-warning py-2 px-3 mb-3">
                                        <small>
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Chat is disabled for this brand
                                        </small>
                                    </div>
                                )}

                                <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                    <div className="form-check form-switch flex-fill">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id={`brand-toggle-${brand._id}`}
                                            checked={brand.isActive}
                                            onChange={() => handleToggleBrand(brand._id, brand.isActive)}
                                        />
                                        <label className="form-check-label" htmlFor={`brand-toggle-${brand._id}`}>
                                            {brand.isActive ? 'Enabled' : 'Disabled'}
                                        </label>
                                    </div>
                                    <Link to={`/admin/brands/${brand._id}`} className="btn btn-outline-primary btn-sm">
                                        Edit
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {brands.length === 0 && (
                <div className="text-center py-5">
                    <p className="text-muted">No brands found</p>
                </div>
            )}
        </div>
    );
}

export default BrandList;
